import { useState, useCallback, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { invokeEdgeFunction } from "@/lib/fetchJson";
import { friendlyErrorMessage } from "@/lib/friendlyError";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface GitHubConnection {
  connected: boolean;
  username: string | null;
  tokenSource: "pat" | "oauth" | null;
  updatedAt: string | null;
}

export interface GitHubRepo {
  repoFullName: string;
  repoUrl: string;
  lastPushedAt: string | null;
  lastCommitSha: string | null;
}

export interface GitHubPushResult {
  success?: boolean;
  repoUrl?: string;
  repoFullName?: string;
  commitSha?: string;
  filesCount?: number;
  skippedCount?: number;
  error?: string;
}

interface PushOptions {
  repoFullName?: string;
  isPrivate?: boolean;
  commitMessage?: string;
}

const EMPTY_CONNECTION: GitHubConnection = {
  connected: false,
  username: null,
  tokenSource: null,
  updatedAt: null,
};

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useGitHubConnection(
  projectId: string | null,
  userId?: string | null
) {
  const [connection, setConnection] =
    useState<GitHubConnection>(EMPTY_CONNECTION);
  const [linkedRepo, setLinkedRepo] = useState<GitHubRepo | null>(null);
  const [loading, setLoading] = useState(true);
  const [pushRunning, setPushRunning] = useState(false);
  const [pushProgress, setPushProgress] = useState("");
  const [pushResult, setPushResult] = useState<GitHubPushResult | null>(null);
  const checkedRef = useRef(false);

  // ─── Check connection status ───────────────────────────────────────
  const checkStatus = useCallback(async () => {
    try {
      const res = await invokeEdgeFunction<{
        connected: boolean;
        username?: string;
        tokenSource?: string;
        updatedAt?: string;
      }>("github-auth", { action: "status" });

      setConnection({
        connected: !!res.connected,
        username: res.username || null,
        tokenSource: (res.tokenSource as "pat" | "oauth") || null,
        updatedAt: res.updatedAt || null,
      });
    } catch {
      // If status check fails, treat as disconnected
      setConnection(EMPTY_CONNECTION);
    } finally {
      setLoading(false);
    }
  }, []);

  // ─── Fetch linked repo for current project ─────────────────────────
  const fetchLinkedRepo = useCallback(async () => {
    if (!projectId) {
      setLinkedRepo(null);
      return;
    }
    try {
      const { data } = await supabase
        .from("project_github_repos")
        .select(
          "github_repo_full_name, github_repo_url, last_pushed_at, last_commit_sha"
        )
        .eq("project_id", projectId)
        .single();

      if (data) {
        setLinkedRepo({
          repoFullName: data.github_repo_full_name,
          repoUrl: data.github_repo_url,
          lastPushedAt: data.last_pushed_at,
          lastCommitSha: data.last_commit_sha,
        });
      } else {
        setLinkedRepo(null);
      }
    } catch {
      setLinkedRepo(null);
    }
  }, [projectId]);

  // ─── Auto-check on mount ───────────────────────────────────────────
  useEffect(() => {
    if (checkedRef.current) return;
    checkedRef.current = true;
    checkStatus();
  }, [checkStatus]);

  useEffect(() => {
    if (projectId) fetchLinkedRepo();
  }, [projectId, fetchLinkedRepo]);

  // ─── Auto-migrate localStorage PAT to DB ───────────────────────────
  useEffect(() => {
    if (loading || connection.connected || !userId) return;
    const legacyPatKey = `dayone.github.pat.${userId}`;
    const legacyPat = localStorage.getItem(legacyPatKey);
    if (!legacyPat) return;

    // Migrate in background — don't block UI
    (async () => {
      try {
        const res = await invokeEdgeFunction<{
          success?: boolean;
          username?: string;
          error?: string;
        }>("github-auth", { action: "connect-pat", pat: legacyPat });
        if (res.success) {
          // Clear all legacy keys
          for (let i = localStorage.length - 1; i >= 0; i--) {
            const k = localStorage.key(i);
            if (
              k &&
              (k.startsWith("dayone.github.connected") ||
                k.startsWith("dayone.github.pat") ||
                k.startsWith("dayone.github.repo"))
            ) {
              localStorage.removeItem(k);
            }
          }
          await checkStatus();
          console.log("[useGitHubConnection] migrated localStorage PAT to DB");
        }
      } catch (err) {
        console.warn("[useGitHubConnection] PAT migration failed:", err);
      }
    })();
  }, [loading, connection.connected, userId, checkStatus]);

  // ─── Connect with PAT ──────────────────────────────────────────────
  const connectWithPat = useCallback(
    async (pat: string): Promise<boolean> => {
      try {
        const res = await invokeEdgeFunction<{
          success?: boolean;
          username?: string;
        }>("github-auth", { action: "connect-pat", pat });

        if (res.success) {
          await checkStatus();
          return true;
        }
        return false;
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : "Failed to connect GitHub";
        throw new Error(friendlyErrorMessage(msg));
      }
    },
    [checkStatus]
  );

  // ─── Start OAuth ──────────────────────────────────────────────────
  const startOAuth = useCallback(
    async (
      redirectUri: string
    ): Promise<{ authorizeUrl: string } | null> => {
      try {
        const res = await invokeEdgeFunction<{ authorizeUrl?: string }>(
          "github-auth",
          { action: "authorize", redirectUri }
        );
        if (res.authorizeUrl) {
          return { authorizeUrl: res.authorizeUrl };
        }
        return null;
      } catch {
        return null;
      }
    },
    []
  );

  // ─── Complete OAuth (exchange code) ────────────────────────────────
  const completeOAuth = useCallback(
    async (code: string): Promise<boolean> => {
      try {
        const res = await invokeEdgeFunction<{
          success?: boolean;
          username?: string;
        }>("github-auth", { action: "callback", code });
        if (res.success) {
          await checkStatus();
          return true;
        }
        return false;
      } catch {
        return false;
      }
    },
    [checkStatus]
  );

  // ─── Disconnect ────────────────────────────────────────────────────
  const disconnect = useCallback(async (): Promise<void> => {
    try {
      await invokeEdgeFunction("github-auth", { action: "disconnect" });
    } catch {
      // Continue cleanup even if edge function fails
    }
    // Clear legacy localStorage keys
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (
        k &&
        (k.startsWith("dayone.github.connected") ||
          k.startsWith("dayone.github.pat") ||
          k.startsWith("dayone.github.repo") ||
          k.startsWith("dayone.github.lastRepo"))
      ) {
        localStorage.removeItem(k);
      }
    }
    setConnection(EMPTY_CONNECTION);
    setLinkedRepo(null);
  }, []);

  // ─── Push to GitHub ────────────────────────────────────────────────
  const pushToGitHub = useCallback(
    async (
      files: Array<{ name: string; content: string }>,
      projectName: string,
      opts?: PushOptions
    ): Promise<GitHubPushResult> => {
      if (!projectId) {
        const r: GitHubPushResult = { error: "No project selected" };
        setPushResult(r);
        return r;
      }

      setPushRunning(true);
      setPushResult(null);
      setPushProgress("Pushing to GitHub...");

      try {
        const res = await invokeEdgeFunction<GitHubPushResult>(
          "push-to-github",
          {
            projectId,
            projectName,
            files,
            repoFullName: opts?.repoFullName,
            isPrivate: opts?.isPrivate,
            commitMessage: opts?.commitMessage,
          }
        );

        setPushResult(res);
        if (res.success) {
          // Refresh linked repo after successful push
          await fetchLinkedRepo();
        }
        return res;
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : "Push to GitHub failed";
        const r: GitHubPushResult = { error: friendlyErrorMessage(msg) };
        setPushResult(r);
        return r;
      } finally {
        setPushRunning(false);
        setPushProgress("");
      }
    },
    [projectId, fetchLinkedRepo]
  );

  // ─── Reset push state ─────────────────────────────────────────────
  const resetPushResult = useCallback(() => {
    setPushResult(null);
    setPushProgress("");
  }, []);

  return {
    connection,
    linkedRepo,
    loading,
    pushRunning,
    pushProgress,
    pushResult,
    checkStatus,
    connectWithPat,
    startOAuth,
    completeOAuth,
    disconnect,
    fetchLinkedRepo,
    pushToGitHub,
    resetPushResult,
  };
}
